import React from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'
import { format } from 'date-fns'

import Bookmark from '~/components/atoms/Bookmark/Bookmark'
import Container from '~/components/atoms/Container/Container'
import Lozenge from '~/components/atoms/Lozenge/Lozenge'
import SocialShare from '~/components/atoms/SocialShare/SocialShare'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import TextBlock from '~/components/atoms/TextBlock/TextBlock'
import ArticleFooterAuthor from '~/components/blocks/ArticleFooterAuthor/ArticleFooterAuthor'
import ArticleHeaderAuthor from '~/components/blocks/ArticleHeaderAuthor/ArticleHeaderAuthor'
import SinglePodcastModule from '~/components/blocks/SinglePodcastModule/SinglePodcastModule'
import SingleVideoModule from '~/components/blocks/SingleVideoModule/SingleVideoModule'
import articleParentTitleLink from '~/utils/articleParentTitleLink'

import styles from '~/styles/pages.module.css'

interface PodcastVideoArticleProps {
  isVideo: boolean
  isPodcast: boolean
  video: any
  podcast: any
  articleHeader: any
  keySummary: any
  renderExpertBlock: (
    location: 'Header' | 'Footer',
    useStickyBehaviour?: boolean
  ) => React.ReactNode
}

/**
 * Renders the single video/podcast page: a centered header (title, author meta
 * and category lozenge), the full-width media player, and any trailing summary
 * text plus the article footer in a centered, max-width column.
 */
const PodcastVideoArticle: React.FC<PodcastVideoArticleProps> = ({
  isVideo,
  isPodcast,
  video,
  podcast,
  articleHeader,
  keySummary,
  renderExpertBlock,
}) => {
  const t = useTranslations('articleContainer')
  const tArticle = useTranslations('articlePage')

  const parentTitle = articleParentTitleLink(
    articleHeader?.articleType,
    articleHeader?.articleTypeSlug,
    articleHeader?.articleTaxonomy,
    articleHeader?.articleTypeTitle
  )

  return (
    <>
      <Container>
        <div className={styles.mediaPage}>
          <div className={styles.mediaPageHeader}>
            <Text
              size={TextSizes.magPrimaryTitleXLrg}
              Tag="h1"
              className={styles.mediaPageTitle}
            >
              {articleHeader.title}
            </Text>

            <div className={styles.mediaPageMeta}>
              <ArticleHeaderAuthor
                author={articleHeader.author}
                reviewedBy={articleHeader.reviewedBy}
                centered
                authorPretext={t('featuring')}
              />
              {(articleHeader?.readtime || articleHeader?.updatedAt) && (
                <div className={styles.mediaPagePublicationInfo}>
                  {articleHeader?.readtime && (
                    <div className={styles.mediaPagePublicationItem}>
                      <Text
                        size={TextSizes.magSecondaryBodySml}
                        className={styles.publicationInfoItemValue}
                      >
                        {tArticle('minuteRead', {
                          readtime: articleHeader.readtime,
                        })}
                      </Text>
                    </div>
                  )}
                  {articleHeader?.updatedAt && (
                    <div className={styles.mediaPagePublicationItem}>
                      <Text
                        size={TextSizes.magSecondaryBodySml}
                        className={styles.publicationInfoItemTitle}
                      >
                        {t('lastUpdated')}
                      </Text>
                      <Text
                        size={TextSizes.magSecondaryBodySml}
                        className={styles.publicationInfoItemValue}
                      >
                        {format(
                          new Date(articleHeader.updatedAt),
                          'd MMMM yyyy'
                        )}
                      </Text>
                    </div>
                  )}
                </div>
              )}
            </div>

            {parentTitle.title && (
              <Lozenge
                title={parentTitle.title}
                link={parentTitle.link}
                hideOnZero={true}
              />
            )}
          </div>

          <div className={styles.mediaPageMediaFull}>
            {isVideo && video && (
              <SingleVideoModule
                video={video}
                title=""
                summary=""
                withContainer={false}
                verticalPadding={false}
                autoPlay={true}
              />
            )}
            {isPodcast && podcast?.podcastUrl && (
              <SinglePodcastModule url={podcast.podcastUrl} />
            )}
          </div>

          <div className={styles.mediaPageBody}>
            {keySummary ? (
              <div className={classNames(styles.keySummary)}>
                <TextBlock
                  body={keySummary}
                  _key={'key_summary'}
                  variant={'magazine'}
                  smallTitle
                  title={t('keyTakeaways')}
                />
                <span className={styles.line} />
              </div>
            ) : (
              (video?.summary || podcast?.summary) && (
                <Text size={TextSizes.bodyLrg}>
                  {video?.summary || podcast?.summary}
                </Text>
              )
            )}

            <div
              className={classNames(
                styles.articleFooter,
                styles.isPodcastOrVideo
              )}
            >
              <div className={styles.articleShare}>
                {/* Bookmark button */}
                <Bookmark />
                <SocialShare
                  articleTitle={articleHeader?.title}
                  shareButtonTheme="magazine"
                />
              </div>
              <ArticleFooterAuthor
                author={articleHeader?.author}
                reviewedBy={articleHeader?.reviewedBy}
                publishDate={articleHeader?.publishDate}
                updatedAt={articleHeader?.updatedAt}
              />
            </div>
          </div>
        </div>
      </Container>
      {renderExpertBlock('Footer')}
    </>
  )
}

export default PodcastVideoArticle
